<template>
  <div
    v-if="canSeeAllDepartments"
    class="department-group-selector"
  >
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      offset-y
    >
      <template #activator="{ on, attrs }">
        <!-- Listeners on a wrapper: the text field only passes them to its input, which excludes the arrow. -->
        <div
          class="department-picker-activator"
          v-bind="attrs"
          v-on="on"
        >
          <v-text-field
            append-icon="$dropdown"
            class="department-picker subtitle-2"
            dense
            hide-details
            outlined
            readonly
            style="max-width: 200px"
            :value="selectedName"
          />
        </div>
      </template>
      <!-- Departments and groups side by side; each list scrolls on its own. -->
      <v-card class="department-group-menu">
        <div class="d-flex">
          <div class="department-group-menu-column departments">
            <v-subheader>
              {{ $tc('labels.department', 2) }}
            </v-subheader>
            <v-list
              class="department-group-menu-list py-0"
              dense
            >
              <v-list-item
                v-for="item in departmentItems"
                :key="item.value"
                class="department-item"
                color="primary"
                :input-value="item.value === selectedValue"
                @click="select(item.value)"
              >
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
          <template v-if="groupItems.length > 0">
            <v-divider vertical />
            <div class="department-group-menu-column groups">
              <v-subheader>
                {{ $tc('labels.departmentGroup', 2) }}
              </v-subheader>
              <v-list
                class="department-group-menu-list py-0"
                dense
              >
                <v-list-item
                  v-for="item in groupItems"
                  :key="item.value"
                  class="department-group-item"
                  color="primary"
                  :input-value="item.value === selectedValue"
                  @click="select(item.value)"
                >
                  <v-list-item-title>
                    {{ item.name }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </div>
          </template>
        </div>
      </v-card>
    </v-menu>
  </div>
  <span
    v-else
    class="title page-title d-inline-block text-truncate"
    :style="pageTitleStyle"
  >
    {{ activeDepartment.name }}
  </span>
</template>

<script>
import _ from 'lodash';
import { getUnsavedChangesDialogProps } from '@/utils';

const DEPARTMENT_PREFIX = 'dept-';
const GROUP_PREFIX = 'group-';

export default {
  data () {
    const selectedValue = this.getActiveSelectionValue();
    return {
      menu: false,
      previousSelectedValue: selectedValue,
      selectedValue
    };
  },
  computed: {
    activeDepartment () {
      return this.$store.getters['org/getActiveDepartment']() || {};
    },
    canSeeAllDepartments () {
      return this.$store.getters['account/canAccessAnyDepartment']();
    },
    departmentGroups () {
      return this.$store.getters['org/getDepartmentGroups']();
    },
    departments () {
      return _.filter(this.$store.state.org.departments, (department) => department.partakeInScheduling);
    },
    departmentItems () {
      return this.departments.map((department) => ({
        name: department.name,
        type: 'department',
        value: `${DEPARTMENT_PREFIX}${department.id}`
      }));
    },
    groupItems () {
      return this.departmentGroups.map((group) => ({
        name: group.name,
        type: 'group',
        value: `${GROUP_PREFIX}${group.id}`
      }));
    },
    selectedName () {
      const item = _.find([...this.departmentItems, ...this.groupItems], (item) => item.value === this.selectedValue);
      return item ? item.name : '';
    },
    pageTitleStyle () {
      return {
        'max-width': '150px'
      };
    }
  },
  methods: {
    applySelection (value) {
      const userId = this.$store.state.account.userId;
      if (value.startsWith(GROUP_PREFIX)) {
        this.$store.commit('account/set_active_department_group_id', {
          id: parseInt(value.substring(GROUP_PREFIX.length)),
          userId
        });
      } else {
        this.$store.commit('account/set_active_department_id', parseInt(value.substring(DEPARTMENT_PREFIX.length)));
        this.$store.commit('account/set_active_department_group_id', { id: null, userId });
      }
    },
    select (value) {
      this.menu = false;
      if (value !== this.selectedValue) {
        this.setActiveSelection(value);
      }
    },
    getActiveSelectionValue () {
      const groupId = this.$store.state.account.activeDepartmentGroupId;
      if (_.some(this.$store.getters['org/getDepartmentGroups'](), (g) => g.id === groupId)) {
        return `${GROUP_PREFIX}${groupId}`;
      }
      let department = this.$store.getters['org/getActiveDepartment']();
      if (!department) {
        department = _.find(this.$store.state.org.departments, (department) => department.partakeInScheduling);
      }
      return department ? `${DEPARTMENT_PREFIX}${department.id}` : null;
    },
    setActiveSelection (value) {
      if (this.$store.getters.hasUnsavedChanges) {
        this.$dialog.confirm(getUnsavedChangesDialogProps(this)).then(() => {
          this.selectedValue = this.previousSelectedValue;
        }).catch(() => {
          this.previousSelectedValue = value;
          this.selectedValue = value;
          this.$store.commit('unmark_all_unsaved_changes');
          this.applySelection(value);
        });
      } else {
        this.previousSelectedValue = value;
        this.selectedValue = value;
        this.applySelection(value);
      }
    }
  }
};
</script>

<style lang="scss">
.department-picker {
  fieldset {
    background: $page-background !important;
  }
  input {
    cursor: pointer;
  }
}
.department-picker-activator {
  cursor: pointer;
}
.department-group-menu {
  .department-group-menu-column {
    max-width: 260px;
    min-width: 200px;
  }
  .department-group-menu-list {
    max-height: 360px;
    overflow-y: auto;
  }
}
</style>
